import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";

import { Container } from "@mui/material";
import { useJwtStorage, useLocalStorage } from "../../hooks/useLocalStorage";
import Link from "@mui/material/Link";

import Paper from "@mui/material/Paper";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";

import isEmpty from "lodash-es/isEmpty";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import TableContainer from "@mui/material/TableContainer";

import DrawerAppBar from "../../components/DrawerAppBar";
import Title from "../../components/Title";
import ProgressBar from "../../components/ProgressBar";

import { doService } from "../../services/ServiceHelper";
import {
  getActivity,
  getRestrictedEnrollmentStudents,
} from "../../services/ActivityService";
import TableRowNoResults from "../../components/TableRowNoResults";
import {
  StripedTableRow,
  StripedTableCell,
  StripedTableHeadCell,
} from "../../components/StripedTable";

const WhoCanViewActivity = () => {
  const [_school] = useLocalStorage("Auth", "school", null);
  const [_jwt] = useJwtStorage();
  const [_showProgress, setShowProgress] = useState(false);
  const [_searchPerformed, setSearchPerformed] = useState(false);

  const [_activityDetails, setActivityDetails] = useState({});
  const [_students, setStudents] = useState([]);

  const { eid } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    initContent();
  }, []);

  const initContent = () => {
    setShowProgress(true);
    doService(getActivity(_school, _jwt, eid), (results) => {
      setActivityDetails(results.data.activity);
      setSearchPerformed(true);

      setShowProgress(false);
    });

    doService(
      getRestrictedEnrollmentStudents(_school, _jwt, eid),
      (results) => {
        setStudents(results.data);
      },
    );
  };

  const onClickEditActivity = () => {
    navigate(`/teacher/activity/${eid}`);
  };

  return (
    <>
      <DrawerAppBar />

      <Container sx={{ mt: 1 }}>
        <Title text="Who Can View" showBack={true} />
        <ProgressBar showProgress={_showProgress} />

        {_searchPerformed && (
          <>
            <br />
            <Accordion
              disableGutters
              sx={{ boxShadow: 1, p: 0, mt: 1 }}
              expanded
            >
              <AccordionSummary className="accordionTitle">
                Activity Details
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer component={Paper} sx={{ mt: 1 }}>
                  <Table size="small">
                    <TableBody>
                      <StripedTableRow>
                        <StripedTableHeadCell>Name</StripedTableHeadCell>
                        <StripedTableCell>
                          {_activityDetails.name}
                        </StripedTableCell>
                      </StripedTableRow>
                      <StripedTableRow>
                        <StripedTableHeadCell>Description</StripedTableHeadCell>
                        <StripedTableCell>
                          {isEmpty(_activityDetails.description)
                            ? "-- NONE --"
                            : _activityDetails.description}
                        </StripedTableCell>
                      </StripedTableRow>
                      <StripedTableRow>
                        <StripedTableHeadCell>Is Private?</StripedTableHeadCell>
                        <StripedTableCell>
                          {_activityDetails.isPrivate === "Y" ? "YES" : "NO"}
                        </StripedTableCell>
                      </StripedTableRow>
                      <StripedTableRow>
                        <StripedTableHeadCell>
                          Restricted Enrollment?
                        </StripedTableHeadCell>
                        <StripedTableCell>
                          {_activityDetails.restrictedEnrollment === "Y"
                            ? "YES"
                            : "NO"}
                        </StripedTableCell>
                      </StripedTableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
            <br/>

            <Accordion
              disableGutters
              sx={{ boxShadow: 1, p: 0, mt: 1 }}
              expanded
            >
              <AccordionSummary className="accordionTitle">
                Students Who Can View Activity 
                {_activityDetails.restrictedEnrollment === "Y" && (<>{" "}({_students.length})</>)}
              </AccordionSummary>
              <AccordionDetails>
                {_activityDetails.isPrivate === "Y" && (
                  <Alert severity="danger">
                    <AlertTitle>This Activity is PRIVATE.</AlertTitle>
                    NO Students can view this activity.
                  </Alert>
                )}

                {_activityDetails.restrictedEnrollment === "Y" && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <AlertTitle>
                      This Activity has RESTRICTED ENROLLMENT.
                    </AlertTitle>
                    ONLY students you specifically identify will be able to view
                    and select this activity. You can change this by editing the
                    activity <Link onClick={onClickEditActivity}>here</Link>.
                  </Alert>
                )}

                {_activityDetails.restrictedEnrollment === "Y" &&
                  _activityDetails.isPrivate === "N" && (
                    <>
                      The following students can view this activity (from the
                      RESTRICTED ENROLLMENT):
                      <TableContainer component={Paper} sx={{ mt: 1 }}>
                        <Table size="small">
                          <TableHead>
                            <StripedTableRow>
                              <StripedTableCell>Last Name</StripedTableCell>
                              <StripedTableCell>First Name</StripedTableCell>
                              <StripedTableCell>Grade</StripedTableCell>
                              <StripedTableCell>Email</StripedTableCell>
                              <StripedTableCell>From Student List</StripedTableCell>
                            </StripedTableRow>
                          </TableHead>
                          <TableBody>
                            <TableRowNoResults data={_students} />

                            {_students.map((student) => (
                              <StripedTableRow>
                                <StripedTableCell>{student.last_name}</StripedTableCell>
                                <StripedTableCell>{student.first_name}</StripedTableCell>
                                <StripedTableCell>{student.grade}</StripedTableCell>
                                <StripedTableCell>{student.email}</StripedTableCell>
                                <StripedTableCell>{student.userListName}</StripedTableCell>
                              </StripedTableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}

                {_activityDetails.restrictedEnrollment === "N" &&
                  _activityDetails.isPrivate === "N" && (
                    <Alert severity="success">
                      <AlertTitle>
                        ALL STUDENTS can view this activity.
                      </AlertTitle>
                    </Alert>
                  )}
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </Container>
    </>
  );
};

export default WhoCanViewActivity;
